import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Upload, Pencil } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { api, errMsg } from "@/lib/api";
import Layout from "@/components/Layout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "@/components/ui/sonner";

function PeopleTable({ rows, columns, onDelete, onEdit, testidPrefix }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {columns.map((c) => <TableHead key={c.key}>{c.label}</TableHead>)}
          <TableHead className="w-24"></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.length === 0 && (
          <TableRow><TableCell colSpan={columns.length + 1} className="text-center text-slate-400 py-8" data-testid={`${testidPrefix}-empty`}>
            No records yet.
          </TableCell></TableRow>
        )}
        {rows.map((row) => (
          <TableRow key={row.id} data-testid={`${testidPrefix}-row-${row.id}`}>
            {columns.map((c) => <TableCell key={c.key}>{c.render ? c.render(row) : row[c.key]}</TableCell>)}
            <TableCell>
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" data-testid={`edit-${testidPrefix}-${row.id}`}
                  onClick={() => onEdit(row)} className="text-slate-400 hover:text-slate-950 hover:bg-slate-100">
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" data-testid={`delete-${testidPrefix}-${row.id}`}
                  onClick={() => onDelete(row.id)} className="text-slate-400 hover:text-red-600 hover:bg-red-50">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export default function People() {
  const queryClient = useQueryClient();
  const [teacherOpen, setTeacherOpen] = useState(false);
  const [studentOpen, setStudentOpen] = useState(false);
  const [editTeacher, setEditTeacher] = useState(null);
  const [editStudent, setEditStudent] = useState(null);
  const [teacherForm, setTeacherForm] = useState({});
  const [studentForm, setStudentForm] = useState({});
  const [parentOpen, setParentOpen] = useState(false);
  const [editParent, setEditParent] = useState(null);
  const [parentForm, setParentForm] = useState({});
  const [csvFile, setCsvFile] = useState(null);
  const [csvDept, setCsvDept] = useState("");
  const [csvSem, setCsvSem] = useState("");
  const [uploading, setUploading] = useState(false);

  const { data: departments = [] } = useQuery({ queryKey: ["departments"], queryFn: async () => (await api.get("/admin/departments")).data });
  const { data: semesters = [] } = useQuery({ queryKey: ["semesters"], queryFn: async () => (await api.get("/admin/semesters")).data });
  const { data: teachers = [] } = useQuery({ queryKey: ["teachers"], queryFn: async () => (await api.get("/admin/teachers")).data });
  const { data: students = [] } = useQuery({ queryKey: ["students"], queryFn: async () => (await api.get("/admin/students")).data });
  const { data: parents = [] } = useQuery({ queryKey: ["parents"], queryFn: async () => (await api.get("/admin/parents")).data });
  const { data: semSubjects = [] } = useQuery({
    queryKey: ["sem-subjects", studentForm.semester_id],
    queryFn: async () => (await api.get("/admin/subjects", { params: { semester_id: studentForm.semester_id } })).data,
    enabled: !!studentForm.semester_id && studentOpen,
  });
  const electiveGroups = {};
  semSubjects.filter((s) => s.paper_type === "optional").forEach((s) => {
    (electiveGroups[s.elective_group || "Electives"] = electiveGroups[s.elective_group || "Electives"] || []).push(s);
  });

  const deptName = (id) => departments.find((d) => d.id === id)?.name || "—";
  const semName = (id) => semesters.find((s) => s.id === id)?.name || "—";
  const semsForDept = (depId) => semesters.filter((s) => s.department_id === depId);

  const openEditTeacher = (row) => {
    setEditTeacher(row);
    setTeacherForm({ name: row.name, email: row.email, department_id: row.department_id, joining_date: row.joining_date || "", is_hod: !!row.is_hod, password: "",
      employment_type: row.employment_type || "full_time", working_days: row.working_days || [], mobile: row.mobile || "" });
    setTeacherOpen(true);
  };

  const openEditStudent = (row) => {
    setEditStudent(row);
    setStudentForm({ name: row.name, email: row.email, roll_number: row.roll_number,
      department_id: row.department_id, semester_id: row.semester_id, enrollment_date: row.enrollment_date || "",
      elective_subject_ids: row.elective_subject_ids || [], password: "", mobile: row.mobile || "" });
    setStudentOpen(true);
  };

  const createTeacher = async (e) => {
    e.preventDefault();
    try {
      const emp = { employment_type: teacherForm.employment_type || "full_time",
        working_days: teacherForm.employment_type === "part_time" ? (teacherForm.working_days || []) : null,
        mobile: (teacherForm.mobile || "").trim() || null };
      if (editTeacher) {
        const payload = { name: teacherForm.name, email: teacherForm.email, department_id: teacherForm.department_id,
          joining_date: teacherForm.joining_date || null, is_hod: !!teacherForm.is_hod, ...emp };
        if (teacherForm.password) payload.password = teacherForm.password;
        await api.put(`/admin/teachers/${editTeacher.id}`, payload);
        toast.success("Teacher updated");
      } else {
        await api.post("/admin/teachers", { ...teacherForm, ...emp });
        toast.success("Teacher account created");
      }
      setTeacherOpen(false);
      setTeacherForm({});
      setEditTeacher(null);
      queryClient.invalidateQueries({ queryKey: ["teachers"] });
    } catch (e2) { toast.error(errMsg(e2)); }
  };

  const createStudent = async (e) => {
    e.preventDefault();
    try {
      if (editStudent) {
        const payload = { name: studentForm.name, email: studentForm.email, roll_number: studentForm.roll_number,
          department_id: studentForm.department_id, semester_id: studentForm.semester_id,
          enrollment_date: studentForm.enrollment_date, elective_subject_ids: studentForm.elective_subject_ids || [],
          mobile: (studentForm.mobile || "").trim() || null };
        if (studentForm.password) payload.password = studentForm.password;
        await api.put(`/admin/students/${editStudent.id}`, payload);
        toast.success("Student updated");
      } else {
        const { data: stu } = await api.post("/admin/students", studentForm);
        if (studentForm.parent_name && studentForm.parent_email && studentForm.parent_password) {
          try {
            await api.post("/admin/parents", {
              name: studentForm.parent_name, email: studentForm.parent_email,
              password: studentForm.parent_password, mobile: studentForm.parent_mobile || "",
              student_id: stu.id,
            });
            toast.success("Student + parent account created");
          } catch (pe) {
            toast.warning(`Student created, but parent account failed: ${errMsg(pe)}`);
          }
        } else {
          toast.success("Student account created");
        }
      }
      setStudentOpen(false);
      setStudentForm({});
      setEditStudent(null);
      queryClient.invalidateQueries({ queryKey: ["students"] });
      queryClient.invalidateQueries({ queryKey: ["parents"] });
    } catch (e2) { toast.error(errMsg(e2)); }
  };

  const openEditParent = (row) => {
    setEditParent(row);
    setParentForm({ name: row.name, email: row.email, mobile: row.mobile || "", student_id: row.student_id, password: "" });
    setParentOpen(true);
  };

  const saveParent = async (e) => {
    e.preventDefault();
    try {
      if (editParent) {
        const payload = { name: parentForm.name, email: parentForm.email,
          mobile: parentForm.mobile || "", student_id: parentForm.student_id };
        if (parentForm.password) payload.password = parentForm.password;
        await api.put(`/admin/parents/${editParent.id}`, payload);
        toast.success("Parent updated");
      } else {
        await api.post("/admin/parents", { name: parentForm.name, email: parentForm.email,
          password: parentForm.password, mobile: parentForm.mobile || "", student_id: parentForm.student_id });
        toast.success("Parent account created");
      }
      setParentOpen(false);
      setParentForm({});
      setEditParent(null);
      queryClient.invalidateQueries({ queryKey: ["parents"] });
    } catch (e2) { toast.error(errMsg(e2)); }
  };

  const uploadCsv = async () => {
    if (!csvFile || !csvDept || !csvSem) {
      toast.error("Choose a CSV file, department and semester first");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", csvFile);
      fd.append("department_id", csvDept);
      fd.append("semester_id", csvSem);
      const { data } = await api.post("/admin/students/bulk", fd);
      toast.success(`Imported ${data.created} students (${data.skipped} skipped)`);
      if (data.errors?.length) toast.warning(data.errors.slice(0, 3).join(" · "));
      setCsvFile(null);
      queryClient.invalidateQueries({ queryKey: ["students"] });
    } catch (e2) { toast.error(errMsg(e2)); }
    finally { setUploading(false); }
  };

  const deleteUser = async (role, id) => {
    try {
      await api.delete(`/admin/${role}s/${id}`);
      toast.success("Deleted");
      queryClient.invalidateQueries({ queryKey: [`${role}s`] });
    } catch (e2) { toast.error(errMsg(e2)); }
  };

  const DeptSelect = ({ value, onChange, testid }) => (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder="Select department" /></SelectTrigger>
      <SelectContent>
        {departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name} ({d.code})</SelectItem>)}
      </SelectContent>
    </Select>
  );

  const SemSelect = ({ value, onChange, deptId, testid }) => (
    <Select value={value} onValueChange={onChange} disabled={!deptId}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder={deptId ? "Select semester" : "Pick department first"} /></SelectTrigger>
      <SelectContent>
        {semsForDept(deptId).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  return (
    <Layout title="People">
      <Tabs defaultValue="teachers">
        <TabsList className="bg-white border border-slate-200" data-testid="people-tabs">
          <TabsTrigger value="teachers" data-testid="tab-teachers">Teachers</TabsTrigger>
          <TabsTrigger value="students" data-testid="tab-students">Students</TabsTrigger>
          <TabsTrigger value="parents" data-testid="tab-parents">Parents</TabsTrigger>
        </TabsList>

        <TabsContent value="teachers" className="mt-6">
          <div className="bg-white border border-slate-200 rounded-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="font-heading text-lg font-semibold tracking-tight text-slate-950">Teachers</h2>
              <Dialog open={teacherOpen} onOpenChange={(o) => { setTeacherOpen(o); if (!o) { setEditTeacher(null); setTeacherForm({}); } }}>
                <DialogTrigger asChild>
                  <Button size="sm" data-testid="add-teacher-btn" className="bg-slate-950 hover:bg-slate-800 text-white">
                    <Plus className="h-4 w-4" /> Add Teacher
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-white rounded-md">
                  <DialogHeader><DialogTitle>{editTeacher ? "Edit Teacher" : "Add Teacher"}</DialogTitle></DialogHeader>
                  <form onSubmit={createTeacher} className="space-y-4">
                    <div className="space-y-1.5"><Label>Full name</Label>
                      <Input data-testid="teacher-name-input" required value={teacherForm.name || ""}
                        onChange={(e) => setTeacherForm({ ...teacherForm, name: e.target.value })} /></div>
                    <div className="space-y-1.5"><Label>Email</Label>
                      <Input data-testid="teacher-email-input" type="email" required value={teacherForm.email || ""}
                        onChange={(e) => setTeacherForm({ ...teacherForm, email: e.target.value })} /></div>
                    <div className="space-y-1.5"><Label>Mobile number (optional — can be used to sign in)</Label>
                      <Input data-testid="teacher-mobile-input" value={teacherForm.mobile || ""}
                        onChange={(e) => setTeacherForm({ ...teacherForm, mobile: e.target.value })} /></div>
                    <div className="space-y-1.5"><Label>{editTeacher ? "New password (leave blank to keep current)" : "Password"}</Label>
                      <Input data-testid="teacher-password-input" type="text" required={!editTeacher} minLength={8} value={teacherForm.password || ""}
                        onChange={(e) => setTeacherForm({ ...teacherForm, password: e.target.value })} /></div>
                    <div className="space-y-1.5"><Label>Department</Label>
                      <DeptSelect testid="teacher-department-select" value={teacherForm.department_id || ""}
                        onChange={(v) => setTeacherForm({ ...teacherForm, department_id: v })} /></div>
                    <div className="space-y-1.5"><Label>Joining date (leave accrues from this month)</Label>
                      <Input data-testid="teacher-joining-input" type="date" value={teacherForm.joining_date || ""}
                        onChange={(e) => setTeacherForm({ ...teacherForm, joining_date: e.target.value })} /></div>
                    <div className="flex items-center gap-3 pt-1">
                      <Switch checked={!!teacherForm.is_hod} data-testid="teacher-hod-toggle"
                        onCheckedChange={(v) => setTeacherForm({ ...teacherForm, is_hod: v })} />
                      <Label>HOD — can view & download all attendance/leave records (no editing)</Label>
                    </div>
                    <div className="space-y-1.5"><Label>Employment type</Label>
                      <Select value={teacherForm.employment_type || "full_time"}
                        onValueChange={(v) => setTeacherForm({ ...teacherForm, employment_type: v, working_days: teacherForm.working_days || [] })}>
                        <SelectTrigger data-testid="teacher-employment-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="full_time" data-testid="employment-full">Full time</SelectItem>
                          <SelectItem value="part_time" data-testid="employment-part">Part time</SelectItem>
                        </SelectContent>
                      </Select></div>
                    {teacherForm.employment_type === "part_time" && (
                      <div className="space-y-1.5" data-testid="working-days-picker">
                        <Label>Working days — all other days are weekly holidays for this teacher</Label>
                        <div className="flex flex-wrap gap-2">
                          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d, i) => {
                            const on = (teacherForm.working_days || []).includes(i);
                            return (
                              <button type="button" key={d} data-testid={`working-day-${i}`}
                                onClick={() => {
                                  const cur = new Set(teacherForm.working_days || []);
                                  if (on) { cur.delete(i); } else { cur.add(i); }
                                  setTeacherForm({ ...teacherForm, working_days: [...cur].sort((a, b) => a - b) });
                                }}
                                className={`px-3 py-1.5 rounded-md text-xs border transition-colors ${on
                                  ? "bg-slate-950 text-white border-slate-950"
                                  : "bg-white text-slate-600 border-slate-300 hover:border-slate-500"}`}>
                                {d}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    <Button type="submit" data-testid="teacher-submit-btn" className="w-full bg-slate-950 hover:bg-slate-800 text-white">
                      {editTeacher ? "Save teacher" : "Create teacher"}</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            <PeopleTable rows={teachers} testidPrefix="teacher" onDelete={(id) => deleteUser("teacher", id)} onEdit={openEditTeacher}
              columns={[
                { key: "name", label: "Name" },
                { key: "email", label: "Email" },
                { key: "mobile", label: "Mobile", render: (r) => r.mobile || "—" },
                { key: "department_id", label: "Department", render: (r) => deptName(r.department_id) },
                { key: "is_hod", label: "HOD", render: (r) => r.is_hod ? <Badge variant="outline" className="bg-blue-50 text-[#002FA7] border-blue-200">HOD</Badge> : "—" },
                { key: "employment_type", label: "Type", render: (r) => r.employment_type === "part_time"
                  ? <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">Part time</Badge> : "Full time" },
              ]} />
          </div>
        </TabsContent>

        <TabsContent value="students" className="mt-6 space-y-6">
          <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="csv-import-card">
            <h2 className="font-heading text-lg font-semibold tracking-tight text-slate-950 mb-1">Bulk import students (CSV)</h2>
            <p className="text-sm text-slate-500 mb-4">CSV columns: <span className="font-data text-xs">name, email, roll_number</span> (optional: <span className="font-data text-xs">password</span>, defaults to Student@123; <span className="font-data text-xs">enrollment_date</span> as YYYY-MM-DD, defaults to today). All rows are assigned to the selected department + semester.</p>
            <div className="border border-dashed border-slate-300 rounded-md p-6 flex flex-col md:flex-row items-center gap-4">
              <Input type="file" accept=".csv" data-testid="csv-file-input" className="md:max-w-xs"
                onChange={(e) => setCsvFile(e.target.files?.[0] || null)} />
              <div className="w-full md:w-56"><DeptSelect testid="csv-department-select" value={csvDept} onChange={(v) => { setCsvDept(v); setCsvSem(""); }} /></div>
              <div className="w-full md:w-56"><SemSelect testid="csv-semester-select" value={csvSem} onChange={setCsvSem} deptId={csvDept} /></div>
              <Button onClick={uploadCsv} disabled={uploading} data-testid="csv-upload-btn"
                className="bg-slate-950 hover:bg-slate-800 text-white">
                <Upload className="h-4 w-4" /> {uploading ? "Uploading…" : "Import"}
              </Button>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="font-heading text-lg font-semibold tracking-tight text-slate-950">Students</h2>
              <Dialog open={studentOpen} onOpenChange={(o) => { setStudentOpen(o); if (!o) { setEditStudent(null); setStudentForm({}); } }}>
                <DialogTrigger asChild>
                  <Button size="sm" data-testid="add-student-btn" className="bg-slate-950 hover:bg-slate-800 text-white">
                    <Plus className="h-4 w-4" /> Add Student
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-white rounded-md max-h-[90vh] overflow-y-auto">
                  <DialogHeader><DialogTitle>{editStudent ? "Edit Student" : "Add Student"}</DialogTitle></DialogHeader>
                  <form onSubmit={createStudent} className="space-y-4">
                    <div className="space-y-1.5"><Label>Full name</Label>
                      <Input data-testid="student-name-input" required value={studentForm.name || ""}
                        onChange={(e) => setStudentForm({ ...studentForm, name: e.target.value })} /></div>
                    <div className="space-y-1.5"><Label>Email</Label>
                      <Input data-testid="student-email-input" type="email" required value={studentForm.email || ""}
                        onChange={(e) => setStudentForm({ ...studentForm, email: e.target.value })} /></div>
                    <div className="space-y-1.5"><Label>Roll number</Label>
                      <Input data-testid="student-roll-input" required value={studentForm.roll_number || ""}
                        onChange={(e) => setStudentForm({ ...studentForm, roll_number: e.target.value })} /></div>
                    <div className="space-y-1.5"><Label>Mobile number (optional — can be used to sign in)</Label>
                      <Input data-testid="student-mobile-input" value={studentForm.mobile || ""}
                        onChange={(e) => setStudentForm({ ...studentForm, mobile: e.target.value })} /></div>
                    <div className="space-y-1.5"><Label>Enrollment date (attendance counts from this date) <span className="text-red-600">*</span></Label>
                      <Input data-testid="student-enrollment-input" type="date" required value={studentForm.enrollment_date || ""}
                        onChange={(e) => setStudentForm({ ...studentForm, enrollment_date: e.target.value })} /></div>
                    <div className="space-y-1.5"><Label>{editStudent ? "New password (leave blank to keep current)" : "Password"}</Label>
                      <Input data-testid="student-password-input" type="text" required={!editStudent} minLength={8} value={studentForm.password || ""}
                        onChange={(e) => setStudentForm({ ...studentForm, password: e.target.value })} /></div>
                    <div className="space-y-1.5"><Label>Department</Label>
                      <DeptSelect testid="student-department-select" value={studentForm.department_id || ""}
                        onChange={(v) => setStudentForm({ ...studentForm, department_id: v, semester_id: "" })} /></div>
                    <div className="space-y-1.5"><Label>Semester</Label>
                      <SemSelect testid="student-semester-select" value={studentForm.semester_id || ""}
                        onChange={(v) => setStudentForm({ ...studentForm, semester_id: v, elective_subject_ids: [] })} deptId={studentForm.department_id} /></div>
                    {Object.keys(electiveGroups).length > 0 && (
                      <div className="space-y-2" data-testid="electives-picker">
                        <Label>Optional papers (map one per group)</Label>
                        {Object.entries(electiveGroups).map(([group, subs]) => (
                          <div key={group} className="border border-slate-200 rounded-md p-3">
                            <p className="text-xs font-medium text-slate-500 mb-2">{group}</p>
                            <div className="space-y-1.5">
                              {subs.map((s) => {
                                const selected = (studentForm.elective_subject_ids || []).includes(s.id);
                                return (
                                  <button key={s.id} type="button" data-testid={`elective-${s.id}`}
                                    onClick={() => {
                                      const groupIds = subs.map((x) => x.id);
                                      const rest = (studentForm.elective_subject_ids || []).filter((id) => !groupIds.includes(id));
                                      setStudentForm({ ...studentForm, elective_subject_ids: selected ? rest : [...rest, s.id] });
                                    }}
                                    className={`w-full text-left px-3 py-1.5 rounded-md border text-sm transition-colors duration-150 ${
                                      selected ? "bg-slate-950 text-white border-slate-950" : "bg-white text-slate-600 border-slate-300 hover:border-slate-950"}`}>
                                    {s.name} ({s.code})
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {!editStudent && (
                      <div className="border border-slate-200 rounded-md p-3 space-y-3" data-testid="parent-seed-section">
                        <p className="text-xs font-medium text-slate-500">Parent account (optional — created along with the student)</p>
                        <div className="space-y-1.5"><Label>Parent name</Label>
                          <Input data-testid="seed-parent-name" value={studentForm.parent_name || ""}
                            onChange={(e) => setStudentForm({ ...studentForm, parent_name: e.target.value })} /></div>
                        <div className="space-y-1.5"><Label>Parent mobile number</Label>
                          <Input data-testid="seed-parent-mobile" value={studentForm.parent_mobile || ""}
                            onChange={(e) => setStudentForm({ ...studentForm, parent_mobile: e.target.value })} /></div>
                        <div className="space-y-1.5"><Label>Parent email (login)</Label>
                          <Input data-testid="seed-parent-email" type="email" value={studentForm.parent_email || ""}
                            onChange={(e) => setStudentForm({ ...studentForm, parent_email: e.target.value })} /></div>
                        <div className="space-y-1.5"><Label>Parent password</Label>
                          <Input data-testid="seed-parent-password" type="text" minLength={8} value={studentForm.parent_password || ""}
                            onChange={(e) => setStudentForm({ ...studentForm, parent_password: e.target.value })} /></div>
                      </div>
                    )}
                    <Button type="submit" data-testid="student-submit-btn" className="w-full bg-slate-950 hover:bg-slate-800 text-white">
                      {editStudent ? "Save student" : "Create student"}</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            <PeopleTable rows={students} testidPrefix="student" onDelete={(id) => deleteUser("student", id)} onEdit={openEditStudent}
              columns={[
                { key: "roll_number", label: "Roll No" },
                { key: "name", label: "Name" },
                { key: "email", label: "Email" },
                { key: "mobile", label: "Mobile", render: (r) => r.mobile || "—" },
                { key: "department_id", label: "Department", render: (r) => deptName(r.department_id) },
                { key: "semester_id", label: "Semester", render: (r) => semName(r.semester_id) },
              ]} />
          </div>
        </TabsContent>
        <TabsContent value="parents" className="mt-6">
          <div className="bg-white border border-slate-200 rounded-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="font-heading text-lg font-semibold tracking-tight text-slate-950">Parent accounts</h2>
              <Dialog open={parentOpen} onOpenChange={(o) => { setParentOpen(o); if (!o) { setEditParent(null); setParentForm({}); } }}>
                <DialogTrigger asChild>
                  <Button size="sm" data-testid="add-parent-btn" className="bg-slate-950 hover:bg-slate-800 text-white">
                    <Plus className="h-4 w-4" /> Add Parent
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-white rounded-md max-h-[90vh] overflow-y-auto">
                  <DialogHeader><DialogTitle>{editParent ? "Edit Parent" : "Add Parent"}</DialogTitle></DialogHeader>
                  <form onSubmit={saveParent} className="space-y-4">
                    <div className="space-y-1.5"><Label>Parent full name</Label>
                      <Input data-testid="parent-name-input" required value={parentForm.name || ""}
                        onChange={(e) => setParentForm({ ...parentForm, name: e.target.value })} /></div>
                    <div className="space-y-1.5"><Label>Mobile number</Label>
                      <Input data-testid="parent-mobile-input" required value={parentForm.mobile || ""}
                        onChange={(e) => setParentForm({ ...parentForm, mobile: e.target.value })} /></div>
                    <div className="space-y-1.5"><Label>Email (login)</Label>
                      <Input data-testid="parent-email-input" type="email" required value={parentForm.email || ""}
                        onChange={(e) => setParentForm({ ...parentForm, email: e.target.value })} /></div>
                    <div className="space-y-1.5"><Label>{editParent ? "New password (leave blank to keep current)" : "Password"}</Label>
                      <Input data-testid="parent-password-input" type="text" required={!editParent} minLength={8} value={parentForm.password || ""}
                        onChange={(e) => setParentForm({ ...parentForm, password: e.target.value })} /></div>
                    <div className="space-y-1.5"><Label>Linked student</Label>
                      <Select value={parentForm.student_id || ""} onValueChange={(v) => setParentForm({ ...parentForm, student_id: v })}>
                        <SelectTrigger data-testid="parent-student-select"><SelectValue placeholder="Select student" /></SelectTrigger>
                        <SelectContent>
                          {students.map((s) => (
                            <SelectItem key={s.id} value={s.id} data-testid={`parent-student-${s.id}`}>
                              {s.name} ({s.roll_number})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select></div>
                    <Button type="submit" data-testid="parent-submit-btn" className="w-full bg-slate-950 hover:bg-slate-800 text-white">
                      {editParent ? "Save parent" : "Create parent"}</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            <PeopleTable rows={parents} testidPrefix="parent" onDelete={(id) => deleteUser("parent", id)} onEdit={openEditParent}
              columns={[
                { key: "name", label: "Name" },
                { key: "mobile", label: "Mobile" },
                { key: "email", label: "Email" },
                { key: "student_name", label: "Student" },
                { key: "student_roll", label: "Student Roll No" },
              ]} />
          </div>
        </TabsContent>
      </Tabs>
    </Layout>
  );
}
